import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import { useCancelAdminInvoiceReport } from '@/features/admin_invoice_reports/components/ConfirmCancelModal/hooks/useCancelAdminInvoiceReport';
import { useTranslation } from '@/i18n';
import {
  ButtonV2Props,
  ModalWithoutPortal,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import styles from './ConfirmCancelModal.module.scss';
import { useConfirmCancelModal } from './context/ConfirmCancelModalContext';

const cx = classnames.bind(styles);

type ConfirmCancelModalDetailProps = {
  number?: string | null;
  title?: string | null;
  apPayeeName?: string | null;
  totalAmount: string;
};

const ConfirmCancelModalDetail: FC<ConfirmCancelModalDetailProps> = memo(
  ({ number, title, apPayeeName, totalAmount }) => {
    const { t } = useTranslation();
    return (
      <div className={cx('detail-table')}>
        {number && (
          <div className={cx('detail-row')}>
            <span className={cx('detail-label')}>
              {t('invoice_reports.table.columns.application_number')}
            </span>
            <span className={cx('detail-value')}>{number}</span>
          </div>
        )}
        {title && (
          <div className={cx('detail-row')}>
            <span className={cx('detail-label')}>
              {t('invoice_reports.table.columns.title')}
            </span>
            <span className={cx('detail-value')}>{title}</span>
          </div>
        )}
        <div className={cx('detail-row')}>
          <span className={cx('detail-label')}>
            {t('invoice_reports.table.columns.payee')}
          </span>
          <span className={cx('detail-value')}>{apPayeeName}</span>
        </div>
        <div className={cx('detail-row')}>
          <span className={cx('detail-label')}>
            {t('invoice_reports.table.columns.total_amount')}
          </span>
          <span className={cx('detail-value')}>{totalAmount}</span>
        </div>
      </div>
    );
  }
);
ConfirmCancelModalDetail.displayName = 'ConfirmCancelModalDetail';

export const ConfirmCancelModal: FC = memo(() => {
  const {
    cancelModalOpen,
    cancelRecord,
    onCloseConfirmCancelModal,
    refetch,
    onDrawerClose,
    selectedId,
  } = useConfirmCancelModal();

  const { t } = useTranslation();
  const { cancelReport } = useCancelAdminInvoiceReport({
    refetch,
    onDrawerClose,
    selectedId,
  });

  const handleConfirm = useCallback(async () => {
    if (!cancelRecord) return;
    await cancelReport(cancelRecord.id)();
    onCloseConfirmCancelModal();
  }, [cancelRecord, cancelReport, onCloseConfirmCancelModal]);

  const closeButtonProps: ButtonV2Props = useMemo(
    () => ({
      isTertiary: true,
      onClick: onCloseConfirmCancelModal,
      size: 'md',
      children: t('cancel'),
    }),
    [onCloseConfirmCancelModal, t]
  );

  const confirmButtonProps: ButtonV2Props = useMemo(
    () => ({
      color: 'danger',
      onClick: handleConfirm,
      size: 'md',
      children: t('ap_report_status_canceled'),
    }),
    [handleConfirm, t]
  );

  return (
    <ModalWithoutPortal
      open={cancelModalOpen}
      onClose={onCloseConfirmCancelModal}
      size='md'
      title={t('ap_report_status_canceled')}
      mask
      footer={
        <ConfirmationModalFooter
          cancelButton={closeButtonProps}
          okButton={confirmButtonProps}
        />
      }
    >
      <div>
        <p className={cx('confirm-message')}>
          {t('invoice_reports.action_menu.cancel_confirm_message')}
        </p>
        {cancelRecord && (
          <ConfirmCancelModalDetail
            number={cancelRecord.number ?? null}
            title={cancelRecord.title ?? null}
            apPayeeName={cancelRecord.apPayeeName ?? null}
            totalAmount={cancelRecord.totalAmount}
          />
        )}
      </div>
    </ModalWithoutPortal>
  );
});

ConfirmCancelModal.displayName = 'ConfirmCancelModal';
